import { ref, onUnmounted, getCurrentInstance } from 'vue'
import store from '@/store'
import { Popover } from 'bootstrap'

export function useSvgRenderer() {
    const svgRendered = ref(null)
    const loading = ref(false)

    const instance = getCurrentInstance()
    const config = instance?.proxy?.config ?? store.getters.getConfig
    const originalColor = '#0CBA31'
    const seleccionadaColor = '#457ad1'
    const bloqueadaColor = '#BF95C4'
    const enSesionColor = 'yellow'
    const vendidaAbonosColor = 'black'
    const vendidaEventosColor = 'red'
    const bloqueadaEventosColor = 'purple'
    const enSesionEventosColor = 'orange'
    const abonadoNoRenovadoColor = '#B05D04'
    const popovers = []
    const clickListeners = []

    function getButacaId(circle) {
        const idParts = circle.id.split('-')
        return idParts[1]
    }

    function getButacaName(butaca) {
        const nButaca = butaca.butaca
        const nFila = butaca.fila
        const sector = butaca.sector_name
        const visibilidad_reducida = butaca.visibilidad_reducida
        const porcentaje_visibilidad = butaca.porcentaje_visibilidad
        let name = ''

        if (sector) {
            name += `Sector: ${sector} - `
        }

        if (!nButaca && !nFila) {
            name += `Plaza: ${butaca.numero}`
        } else {
            name += `Fila: ${nFila} - Butaca: ${nButaca}`
        }

        if (visibilidad_reducida) {
            name += '<span class="text-danger"> Visibilidad Reducida</span>'
        }

        if (porcentaje_visibilidad) {
            name += `<span class="text-danger">: ${porcentaje_visibilidad} %</span>`
        }

        return name
    }

    function drawPopover(circle, plaza, title = null, content = '') {
        if (plaza.ocupada) {
            if (plaza.ocupada.bloqueada) {
                let bloqueadaText = 'Plaza bloqueada';
                if (plaza.etiqueta_bloqueo?.name) {
                    bloqueadaText += ` (${plaza.etiqueta_bloqueo.name})`;
                }
                content = `<div><strong>${bloqueadaText}</strong></div>` + content
            } else if (plaza.ocupada.venta && plaza.ocupada.renovado !== false) {
                content = '<div><strong>Vendida</strong></div>' + content
            } else if (plaza.ocupada.sesion) {
                content = '<div><strong>En sesión</strong></div>' + content
            } else if (plaza.ocupada.evento_venta) {
                content = '<div><strong>Vendida en evento</strong></div>' + content
            } else if (plaza.ocupada.evento_bloqueo) {
                content = '<div><strong>Bloqueada en evento</strong></div>' + content
            } else if (plaza.ocupada.evento_sesion) {
                content = '<div><strong>En sesión en evento</strong></div>' + content
            }
        }

        if (!content && plaza.libre) {
            content = '<div><strong>Plaza libre</strong></div>'
        }

        const popoverOptions = {
            title: title,
            content: '<div style="text-align:left">' + content + '</div>',
            trigger: 'hover',
            html: true,
            container: 'body'
        }

        const popover = new Popover(circle, popoverOptions)
        popovers.push(popover)
    }

    function disposePopovers() {
        popovers.forEach(p => {
            try { p.dispose() } catch (e) {
                console.warn('Error al limpiar popover:', e)
            }
        })
        popovers.length = 0
    }

    function disposeClickListeners() {
        clickListeners.forEach(({ element, handler, eventType }) => {
            element.removeEventListener(eventType, handler)
        })
        clickListeners.length = 0
    }

    function renderLeafletSvg(svg, plazas, { onPlazaClick, onPlazaVentaClick, isPanning } = {}) {
        disposePopovers()
        disposeClickListeners()

        const plazasFiltered = plazas.filter(plaza => plaza.svg_id)
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = svg

        const butacas = tempDiv.querySelectorAll('[id^="butaca"]')

        butacas.forEach(circle => {
            circle.setAttribute('seleccionado', 'false')
            circle.setAttribute('opacity', '0.8')
            const svg_id = getButacaId(circle)
            const plaza = plazasFiltered.find(pl => pl.svg_id == svg_id)
            if (plaza) {
                const title = getButacaName(plaza)

                if (plaza.libre && !plaza.ocupada.bloqueada) {
                    drawPopover(circle, plaza, title)
                    circle.style.cursor = 'pointer'
                    circle.style.transition = 'fill 0.2s'
                    circle.setAttribute('fill', originalColor)

                    if (onPlazaClick) {
                        const clickHandler = () => {
                            let isSeleccionado = JSON.parse(circle.getAttribute('seleccionado'))
                            setTimeout(() => {
                                if (isPanning && isPanning.value) return
                                if (!isSeleccionado) {
                                    circle.setAttribute('fill', seleccionadaColor)
                                    circle.setAttribute('seleccionado', 'true')
                                } else {
                                    circle.setAttribute('fill', originalColor)
                                    circle.setAttribute('seleccionado', 'false')
                                }
                                onPlazaClick(plaza)
                            }, 150)
                        }
                        circle.addEventListener('click', clickHandler)
                        clickListeners.push({ element: circle, handler: clickHandler, eventType: 'click' })
                    }
                } else if (plaza.ocupada && plaza.ocupada.bloqueada) {
                    let bloqueadaContent = '';
                    if (plaza.info_ocupada && plaza.info_ocupada.concepto) {
                        bloqueadaContent += `<div><strong>Concepto: </strong>${plaza.info_ocupada.concepto}</div>`;
                    }
                    drawPopover(circle, plaza, title, bloqueadaContent)
                    const color = plaza.etiqueta_bloqueo?.color || bloqueadaColor
                    circle.setAttribute('fill', color)
                    if (onPlazaClick) {
                        const clickHandler = () => {
                            let isSeleccionado = JSON.parse(circle.getAttribute('seleccionado'))
                            setTimeout(() => {
                                if (isPanning && isPanning.value) return
                                if (!isSeleccionado) {
                                    circle.setAttribute('fill', seleccionadaColor)
                                    circle.setAttribute('seleccionado', 'true')
                                } else {
                                    circle.setAttribute('fill', color)
                                    circle.setAttribute('seleccionado', 'false')
                                }
                                onPlazaClick(plaza)
                            }, 150)
                        }
                        circle.addEventListener('click', clickHandler)
                        clickListeners.push({ element: circle, handler: clickHandler, eventType: 'click' })
                    }
                } else if (plaza.ocupada && plaza.ocupada.sesion) {
                    circle.setAttribute('ocupada', 'true')
                    circle.style.cursor = 'not-allowed'
                    circle.setAttribute('fill', enSesionColor)
                    drawPopover(circle, plaza, title)
                } else if (plaza.ocupada && plaza.ocupada.venta) {
                    circle.setAttribute('ocupada', 'true')
                    if (plaza.ocupada.renovado === false) {
                        circle.setAttribute('fill', abonadoNoRenovadoColor)
                    } else {
                        circle.setAttribute('fill', vendidaAbonosColor)
                    }
                    let content
                    if (plaza.ocupada.renovado === false) {
                        content = '<div><strong>Abonado no renovado</strong></div>'
                    }
                    if (plaza.info_ocupada?.id_abonado) {
                        circle.style.cursor = 'pointer'
                        if (plaza.info_ocupada?.nombre || plaza.info_ocupada?.apellido) {
                            content = (content || '') + `<div><strong>${[plaza.info_ocupada.nombre, plaza.info_ocupada.apellido].filter(Boolean).join(' ')}</strong></div>`
                        }
                        if (plaza.info_ocupada?.numero_abonado) {
                            content = (content || '') + `<div><strong>Nº Abonado: </strong>${plaza.info_ocupada.numero_abonado}</div>`
                        }
                        if (plaza.info_ocupada?.qr_abonado) {
                            content = (content || '') + `<div><strong>Código QR: </strong>${plaza.info_ocupada.qr_abonado}</div>`
                        }
                        if (onPlazaVentaClick) {
                            const clickHandler = () => {
                                onPlazaVentaClick(plaza)
                            }
                            circle.addEventListener('click', clickHandler)
                            clickListeners.push({ element: circle, handler: clickHandler, eventType: 'click' })
                        }
                    } else {
                        circle.style.cursor = 'not-allowed'
                    }
                    drawPopover(circle, plaza, title, content)
                } else if (plaza.ocupada && plaza.ocupada.evento_bloqueo) {
                    circle.setAttribute('ocupada', 'true')
                    circle.setAttribute('fill', bloqueadaEventosColor)
                    circle.style.cursor = 'not-allowed'
                    drawPopover(circle, plaza, title)
                } else if (plaza.ocupada && plaza.ocupada.evento_venta) {
                    circle.setAttribute('ocupada', 'true')
                    circle.setAttribute('fill', vendidaEventosColor)
                    circle.style.cursor = 'not-allowed'
                    let content
                    if (plaza.info_ocupada.numero_pedido) {
                        circle.style.cursor = 'pointer'
                        content = '<div><strong>Nº del pedido: </strong>' + plaza.info_ocupada.numero_pedido + '</div>'
                        if (onPlazaVentaClick) {
                            const clickHandler = () => {
                                onPlazaVentaClick(plaza)
                            }
                            circle.addEventListener('click', clickHandler)
                            clickListeners.push({ element: circle, handler: clickHandler, eventType: 'click' })
                        }
                    }
                    drawPopover(circle, plaza, title, content)
                } else if (plaza.ocupada && plaza.ocupada.evento_sesion) {
                    circle.setAttribute('ocupada', 'true')
                    circle.setAttribute('fill', enSesionEventosColor)
                    circle.style.cursor = 'not-allowed'
                    drawPopover(circle, plaza, title)
                } else if (plaza.ocupada && !plaza.ocupada.bloqueada && !plaza.ocupada.venta && !plaza.ocupada.sesion && !plaza.ocupada.evento_bloqueo && !plaza.ocupada.evento_venta && !plaza.ocupada.evento_sesion) {
                    circle.setAttribute('ocupada', 'true')
                    circle.setAttribute('fill', abonadoNoRenovadoColor)
                    circle.style.cursor = 'not-allowed'
                    drawPopover(circle, plaza, title)
                }
            }
        })

        svgRendered.value = tempDiv.firstElementChild
    }

    function getSvg() {
        loading.value = true
        const recinto = store.getters.getRecinto
        const params = {
            recinto_id: recinto.id
        }
        return store.dispatch(config.getVuexAlias('front.recinto.svg'), params)
            .then(({ data: { svg } }) => {
                return svg
            })
            .catch(error => {
                console.warn('Error al recuperar el SVG del recinto anexo', error)
            })
            .finally(() => {
                loading.value = false
            })
    }

    function getButacas(listaId, recintoId) {
        loading.value = true
        const params = {
            lista_id: listaId,
            recinto_id: recintoId
        }
        return store.dispatch(config.getVuexAlias('back.abonos.listado-butacas'), params)
            .then(res => res.data.data)
            .finally(() => {
                loading.value = false
            })
    }

    onUnmounted(() => {
        disposePopovers()
        disposeClickListeners()
    })

    return {
        svgRendered,
        loading,
        originalColor,
        renderLeafletSvg,
        getSvg,
        getButacas,
        colors: {
            libre: originalColor,
            bloqueada: bloqueadaColor,
            seleccionada: seleccionadaColor,
            enSesion: enSesionColor,
            vendidaAbonos: vendidaAbonosColor,
            vendidaEventos: vendidaEventosColor,
            bloqueadaEventos: bloqueadaEventosColor,
            enSesionEventos: enSesionEventosColor,
            abonadoNoRenovado: abonadoNoRenovadoColor
        }
    }
}
